import type { CSSProperties, MouseEvent } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fasl } from '@fortawesome/sharp-light-svg-icons';
import { useTheme } from '@mui/material/styles';

import * as solid from './solid';

const icons = {
  ...fasl,
  ...solid,
};

export type IconsSize =
  | '10'
  | '12'
  | '14'
  | '16'
  | '18'
  | '20'
  | '22'
  | '24'
  | '26'
  | '28'
  | '30'
  | '32'
  | '38'
  | '40'
  | '46'
  | '48'
  | '56'
  | '60'
  | '86';

export type IconName = keyof typeof icons;

export const getIconName = (name: string, defaultIcon: IconName = 'unknown'): IconName => {
  // Combine all possible icon names from `icons` and `kitIcons`
  const allIconNames = new Set([...Object.keys(icons)]);

  // Check if the provided name exists in the combined set
  return allIconNames.has(name) ? (name as IconName) : defaultIcon;
};

type PropTypes = {
  name: IconName;
  size?: IconsSize;
  dataQa?: string;
  className?: string;
  color?: string;
  style?: (CSSProperties & any) | undefined;
  onClick?: (event: MouseEvent<SVGSVGElement, MouseEvent> | undefined) => void;
};

const defaultProps: Partial<PropTypes> = {
  size: '24',
  dataQa: '',
  className: '',
  color: undefined,
  onClick: undefined,
};

const Icon = ({ name, size, style, dataQa, className, color, onClick }: PropTypes) => {
  const theme = useTheme();

  // Helper function to resolve theme colors
  const resolveColor = (colorValue?: string) => {
    if (!colorValue) return undefined;

    // Check if it's a theme color token (e.g., 'primary.main', 'grey.400')
    if (colorValue.includes('.')) {
      const [palette, shade] = colorValue.split('.');
      const paletteColor = theme.palette[palette as keyof typeof theme.palette];

      if (paletteColor && typeof paletteColor === 'object' && shade in paletteColor) {
        return (paletteColor as any)[shade];
      }
    }

    // Check if it's a CSS custom property format
    if (colorValue.startsWith('--palette-')) {
      return `var(${colorValue})`;
    }

    // Check if it's a direct color value
    if (
      colorValue.startsWith('#') ||
      colorValue.startsWith('rgb') ||
      colorValue.startsWith('hsl')
    ) {
      return colorValue;
    }

    // Fallback to CSS custom property format
    return `var(--color-${colorValue})`;
  };

  const sizeStyle = {
    height: `${size}px`,
    width: `${size}px`,
  };

  return (
    <FontAwesomeIcon
      className={className}
      data-qa={dataQa}
      style={style ?? sizeStyle}
      icon={icons[name]}
      color={resolveColor(color)}
      // @ts-expect-error typing
      onClick={(e) => onClick?.(e)}
    />
  );
};

Icon.defaultProps = defaultProps;

export default Icon;
